import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetAnalysisWidgetAnalyticsConfig,
  useGetWidgetAnalysisConfig,
  usePutAnalysisWidgetConfig
} from "@/api/widget/useWidgetAPI";
import { CloseIcon } from "@/assets/icons";
import Modal from "@/components/ui/modal";
import { MESSAGE } from "@/constants/message";
import useWidgetConfiguration from "@/hooks/dataset-widget/useWidgetConfiguration";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useToast from "@/hooks/ui/useToast";
import { RootState } from "@/store";
import { addWidget, updateWidget } from "@/store/slice/widgetSlice";
import {
  AggregationType,
  OrderByType,
  PostWidgetAnalysisResponse,
  WidgetConfig,
  WidgetConfigWithoutWidgetId,
  WidgetPreviewData,
  WidgetType
} from "@/types/widget";
import {
  buildAnalysisAreaFilter,
  getColumnDisplayName,
  mapPostAnalysisWidgetAnalyticsPayload,
  mapPostWidgetAnalysisConfigPayload,
  mapWidgetsToPutAnalysisConfig
} from "@/utils/widget";

import FormWidgetConfiguration from "./FormWidgetConfiguration";
import WidgetConfigurationTab from "./WidgetConfigurationTab";

export interface WidgetFormData {
  chart_type: WidgetType;
  label: string;
  dataset_id: string;
  dimension: string;
  measure: string;
  aggregation: AggregationType;
  configuration: {
    order_by: OrderByType;
    dimension_values_shown: number;
  };
  is_default: boolean;
}

interface WidgetConfigurationProps {
  open: boolean;
  onClose: () => void;
  /** True when the modal is opened to configure an existing widget (vs. creating a new one); drives the submit button label and default-widget resolution. */
  isEditing: boolean;
  /** Only meaningful when isEditing is true; create-new flows can omit it (defaults to null). */
  selectedWidgetId?: string | null;
  /** When provided the preview and submit actions use the analysis-scoped analytics API. */
  analysisId?: string;
}

const DEFAULT_FORM_VALUES: WidgetFormData = {
  chart_type: WidgetType.BAR_V,
  label: "",
  dataset_id: "",
  dimension: "",
  measure: "",
  aggregation: AggregationType.COUNT,
  configuration: {
    order_by: OrderByType.DESC,
    dimension_values_shown: 10
  },
  is_default: false
};

const toFormValues = (
  widget: WidgetConfig,
  isDatasetPublished: boolean
): WidgetFormData => ({
  chart_type: widget.chart_type,
  label: widget.label,
  // When the widget's dataset is no longer published, its saved
  // dataset/dimension/measure are stale, so blank them out and force the user
  // to re-select.
  dataset_id: isDatasetPublished ? widget.dataset_id : "",
  dimension: isDatasetPublished ? widget.dimension : "",
  measure: isDatasetPublished ? widget.measure : "",
  aggregation: widget.aggregation,
  configuration: { ...widget.configuration },
  is_default: widget.is_default
});

// Renaming a default widget creates a distinct widget, so it loses its default status.
const resolveIsDefault = (
  current: WidgetFormData,
  editedWidget: WidgetConfig | undefined,
  isEditing: boolean
): boolean => {
  if (!isEditing || !editedWidget) return current.is_default;
  return current.label === editedWidget.label ? editedWidget.is_default : false;
};

const WidgetConfiguration = ({
  open,
  onClose,
  isEditing,
  selectedWidgetId = null,
  analysisId
}: WidgetConfigurationProps) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { showConfirmation } = useConfirmationModal();

  const { mutate: persistAnalysisWidgetConfig } = usePutAnalysisWidgetConfig();

  const { widgets } = useSelector((state: RootState) => state.widget);
  const { datasets, filters, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );
  const { gridsSelected } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  const editedWidget = selectedWidgetId ? widgets[selectedWidgetId] : undefined;

  // true in create mode; false only when editing a widget whose dataset is unpublished.
  const isDatasetPublished =
    !editedWidget || Boolean(datasets[editedWidget.dataset_id]?.data.published);

  const method = useForm<WidgetFormData>({
    defaultValues: editedWidget
      ? toFormValues(editedWidget, isDatasetPublished)
      : DEFAULT_FORM_VALUES
  });

  const [previewData, setPreviewData] = useState<WidgetPreviewData | undefined>(
    undefined
  );

  const {
    watch,
    setValue,
    reset,
    getValues,
    handleSubmit,
    trigger,
    formState: { dirtyFields }
  } = method;

  const watchChartType = watch("chart_type");
  // Subscribe to all fields so the query payload stays in sync with the latest
  // form input.
  const formValue = watch();

  const selectedDatasetEntry = datasets[formValue.dataset_id];

  const measureLabel = useMemo(() => {
    if (!formValue.measure) return undefined;
    return getColumnDisplayName(
      rawDatasets,
      formValue.dataset_id,
      formValue.measure
    );
  }, [rawDatasets, formValue.dataset_id, formValue.measure]);

  const analysisAreaFilter = useMemo(
    () =>
      analysisId
        ? buildAnalysisAreaFilter(
            gridsSelected,
            rawDatasets,
            formValue.dataset_id
          )
        : [],
    [analysisId, gridsSelected, rawDatasets, formValue.dataset_id]
  );

  // Both hooks are always called (React rules). Only the active one refetches —
  // regular is disabled when analysisId is present, analysis is disabled when absent.
  const { isFetching: isFetchingRegular, refetch: refetchWidgetConfig } =
    useGetWidgetAnalysisConfig(
      mapPostWidgetAnalysisConfigPayload(
        formValue,
        filters[formValue.dataset_id] ?? [],
        selectedDatasetEntry?.h3Configuration,
        selectedDatasetEntry?.group_by
      ),
      { enabled: false }
    );

  const {
    isFetching: isFetchingAnalysis,
    refetch: refetchAnalysisWidgetConfig
  } = useGetAnalysisWidgetAnalyticsConfig(
    mapPostAnalysisWidgetAnalyticsPayload(
      formValue,
      analysisId ?? "",
      analysisAreaFilter
    ),
    { enabled: false }
  );

  const isFetchingWidgetAnalysis = analysisId
    ? isFetchingAnalysis
    : isFetchingRegular;
  const refetch = analysisId
    ? refetchAnalysisWidgetConfig
    : refetchWidgetConfig;

  const applyPreviewFromResponse = useCallback(
    (response: PostWidgetAnalysisResponse) => {
      if (response.data?.value !== undefined && response.data?.value !== null) {
        setPreviewData([
          {
            type:
              (response.data.aggregation as AggregationType) ??
              AggregationType.COUNT,
            value: response.data.value,
            measure: response.data.measure || ""
          }
        ]);
        return;
      }
      const preview = response.data?.data || response.data?.rows;
      setPreviewData(
        preview?.slice(0, formValue.configuration.dimension_values_shown) || []
      );
    },
    [formValue.configuration.dimension_values_shown]
  );

  // Measure options excluding breakdown group IDs (BIG_NUMBER and DONUT both
  // exclude them). Pre-computed so we can clear a stale breakdown-group measure
  // when switching to either type.
  const { measureOptions: nonBreakdownMeasureOptions } = useWidgetConfiguration(
    {
      datasetId: formValue.dataset_id,
      widgetType: WidgetType.BIG_NUMBER,
      measure: formValue.measure,
      analysisId
    }
  );

  // Auto-preview when opening an existing widget for editing.
  useEffect(() => {
    if (!isEditing || !isDatasetPublished) return;
    refetch().then(({ data, isError }) => {
      if (!isError && data) applyPreviewFromResponse(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally mount-only: isEditing and isDatasetPublished are fixed for the modal's lifetime

  const handleClickPreview = async () => {
    const { data, isError } = await refetch();
    if (isError || !data) {
      showToast({
        message: MESSAGE.WIDGET_FAILED_TO_PREVIEW_DESCRIPTION,
        type: "error"
      });
      return;
    }
    applyPreviewFromResponse(data);
  };

  const onSubmit = async () => {
    const { data, isError } = await refetch();
    if (isError || !data) {
      showToast({
        message: MESSAGE.WIDGET_FAILED_TO_ADD_DESCRIPTION,
        type: "error"
      });
      return;
    }

    // Deep-copy nested objects before dispatching: Immer deep-freezes the
    // stored state, and shallow spreads in the reducer would otherwise freeze
    // the same `configuration` reference that RHF holds in its internal
    // `_formValues`, breaking subsequent reset() calls.
    const current = getValues();
    const snapshot = {
      ...current,
      is_default: resolveIsDefault(current, editedWidget, isEditing),
      configuration: { ...current.configuration }
    };

    // Compute the full updated widget list BEFORE dispatching to Redux.
    // The `widgets` selector won't reflect the dispatch until the next render,
    // so we build nextWidgetList manually to pass the correct state to the PUT.
    let nextWidgetList: WidgetConfigWithoutWidgetId[];
    if (isEditing && selectedWidgetId) {
      nextWidgetList = Object.values(widgets).map((w) =>
        w.widget_id === selectedWidgetId ? { ...w, ...snapshot } : w
      );
      dispatch(updateWidget({ widgetId: selectedWidgetId, data: snapshot }));
    } else {
      nextWidgetList = [...Object.values(widgets), snapshot];
      dispatch(addWidget({ id: snapshot.dataset_id, widget: snapshot }));
    }

    setPreviewData(undefined);
    onClose();
    showToast({
      title: MESSAGE.WIDGET_SUCCESS_ADDED_TITLE,
      message: MESSAGE.WIDGET_SUCCESS_ADDED_DESCRIPTION,
      type: "success"
    });

    if (analysisId) {
      persistAnalysisWidgetConfig({
        analysisId,
        payload: mapWidgetsToPutAnalysisConfig(nextWidgetList, gridsSelected)
      });
    }
  };

  const handleConfirmClose = useCallback(() => {
    reset(DEFAULT_FORM_VALUES);
    onClose();
    setPreviewData(undefined);
  }, [onClose, reset]);

  const handleClose = useCallback(() => {
    if (Object.keys(dirtyFields).length) {
      showConfirmation({
        title: "Discard Changes?",
        message: MESSAGE.WIDGET_CONFIGURATION_CONFIRMATION_LEAVE,
        cancelLabel: "Close",
        confirmLabel: "Keep Editing",
        onCancel: handleConfirmClose,
        onConfirm: () => {
          /* intentional no-op: "Keep Editing" dismisses the dialog without closing */
        }
      });
    } else handleConfirmClose();
  }, [showConfirmation, dirtyFields, handleConfirmClose]);

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      shouldCloseModalOnClickOutside={false}
      className="z-[999999]"
    >
      <div className="w-[80vw] h-[90vh] bg-white p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-txtHeading">
            Widget Configuration
          </h2>
          <button onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        <FormProvider {...method}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex gap-2 mt-4 h-[calc(90vh-80px)]"
          >
            <FormWidgetConfiguration
              widgetType={watchChartType}
              onClickPreview={handleClickPreview}
              isEditing={isEditing}
              analysisId={analysisId}
            />
            <WidgetConfigurationTab
              widgetType={watchChartType}
              setWidgetType={(type) => {
                setValue("chart_type", type);
                trigger("dimension");
                trigger("measure");
                setPreviewData(undefined);
                // Clear measure if it was a breakdown group ID — invalid for BIG_NUMBER and DONUT.
                if (
                  type === WidgetType.BIG_NUMBER ||
                  type === WidgetType.DONUT
                ) {
                  const currentMeasure = getValues("measure");
                  if (
                    currentMeasure &&
                    !nonBreakdownMeasureOptions.some(
                      (opt) => opt.value === currentMeasure
                    )
                  ) {
                    setValue("measure", "");
                    // An empty measure shrinks aggregation options to COUNT
                    // variants, so reset non-COUNT values (matches FormWidgetConfiguration).
                    setValue("aggregation", AggregationType.COUNT, {
                      shouldValidate: true,
                      shouldDirty: true
                    });
                  }
                }
              }}
              isLoading={isFetchingWidgetAnalysis}
              previewData={previewData}
              isDatasetPublished={isDatasetPublished}
              measureLabel={measureLabel}
              aggregation={formValue.aggregation}
            />
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};

export default WidgetConfiguration;
